import React, { useEffect, useImperativeHandle, useRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { format } from "date-fns";
import {
  cn,
  roundDateToNearestFiveMinutes,
  roundToNearestFive,
} from "lib/utils";
import { Calendar } from "../ui/calendar";
import { CalendarIcon } from "lucide-react";
import { ko } from "date-fns/locale";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import dayjs from "dayjs";

const DateTimePicker = ({
  datetime,
  setDatetime,
  className,
  name,
  isShowDate = true,
  isShowTime = true,
}: {
  datetime: Date;
  className?: string;
  name?: string;
  setDatetime: (datetime: Date) => void;
  isShowDate?: boolean;
  isShowTime?: boolean;
}) => {
  const [savedDatetime, setSavedDatetime] = React.useState<Date | undefined>(
    undefined
  );
  const [dateOnly, setDateOnly] = React.useState<Date | undefined>(undefined);
  const [ampm, setAmpm] = React.useState<string>("오전");
  const [hour, setHour] = React.useState<string>("12");
  const [minute, setMinute] = React.useState<string>("0");
  const [popoverOpen, setPopoverOpen] = React.useState<boolean>(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (popoverOpen && calendarRef.current) {
      const focusableElements = calendarRef.current.querySelectorAll("*");
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }
  }, [popoverOpen]);

  const setCalendarDate = (date: Date | undefined) => {
    setDateOnly(date);
    setPopoverOpen(false);
  };

  useEffect(() => {
    if (savedDatetime !== datetime && datetime) {
      setSavedDatetime(datetime);

      // DateTime은 2024-07-10T10:43:17.000Z 의 형태로 UTC 시간을 가지고 있음
      // 이것을 로컬시간으로 변환하면 한국의 경우 +9시간이 더해져서 나옴
      //const localDateTime = roundDateToNearestFiveMinutes(datetime);
      const _date = roundDateToNearestFiveMinutes(datetime);

      let hours = _date.getHours();
      const _ampm = hours < 12 ? "오전" : "오후";
      const _hour = (hours > 12 ? hours - 12 : hours).toString();
      const _minute = _date.getMinutes().toString();
      const _dateOnly = new Date(
        _date.getFullYear(),
        _date.getMonth(),
        _date.getDate()
      );

      setAmpm(_ampm);
      setHour(_hour);
      setMinute(_minute);
      setDateOnly(_dateOnly);
    } else {
      // UI를 datetime으로 적용
      if (datetime && dateOnly) {
        const year = dateOnly.getFullYear();
        const month = dateOnly.getMonth();
        const day = dateOnly.getDate();
        let hours = 0;
        let minutes = 0;

        if (isShowTime) {
          hours = parseInt(hour);
          if (ampm === "오후" && hours < 12) {
            hours += 12;
          }
          minutes = parseInt(minute);
        }

        const newDate = new Date(year, month, day, hours, minutes, 0, 0);
        const prevDate = new Date(datetime);
        if (newDate.getTime() !== prevDate.getTime()) {
          setDatetime(newDate);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datetime, dateOnly, ampm, hour, minute]);

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <div className="flex items-center gap-2">
          <Button
            variant={"outline"}
            className={cn(
              "w-[180px] justify-start text-left font-normal",
              !dateOnly && "text-muted-foreground",
              !isShowDate && "invisible",
              className
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateOnly ? (
              format(datetime, "PPP", { locale: ko })
            ) : (
              <span>날짜 선택</span>
            )}
          </Button>
          {isShowTime && (
            <>
              <div>
                <Select value={ampm} onValueChange={(v) => setAmpm(v)}>
                  <SelectTrigger className="w-[70px]">
                    <SelectValue placeholder="1" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {["오전", "오후"].map((no: string) => (
                        <SelectItem key={no} value={no}>
                          {no}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select value={hour} onValueChange={(v) => setHour(v)}>
                  <SelectTrigger className="w-[70px]">
                    <SelectValue placeholder="1" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {Array.from({ length: 12 }, (_, i) => i).map(
                        (no: number) => (
                          <SelectItem
                            key={no}
                            value={(ampm === "오전" ? no : no + 1).toString()}
                          >
                            {(ampm === "오전" ? no : no + 1).toString()}
                          </SelectItem>
                        )
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div>시</div>
              <div>
                <Select value={minute} onValueChange={(v) => setMinute(v)}>
                  <SelectTrigger className="w-[70px]">
                    <SelectValue placeholder="1" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {Array.from({ length: 12 }, (_, i) => i).map(
                        (no: number) => (
                          <SelectItem key={no} value={(no * 5).toString()}>
                            {(no * 5).toString()}
                          </SelectItem>
                        )
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div>분</div>
            </>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <div ref={calendarRef} tabIndex={-1}>
          <Calendar
            mode="single"
            selected={dateOnly}
            onSelect={setCalendarDate}
            locale={ko}
            initialFocus
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateTimePicker;
